import { ApiError } from "./client";

const API_BASE = (import.meta.env.VITE_API_BASE_URL || "").replace(/\/$/, "") + "/api/v1";

function buildHeaders(tenantId, extra, token) {
  const h = new Headers(extra);
  h.set("x-tenant-id", tenantId);
  if (!h.has("Accept")) h.set("Accept", "application/json");
  if (token && !h.has("Authorization")) h.set("Authorization", `Bearer ${token}`);
  return h;
}

async function parseResponse(resp) {
  const ct = resp.headers.get("content-type") || "";
  const isJson = ct.includes("application/json");
  const body = isJson ? await resp.json().catch(() => null) : await resp.text();
  if (!resp.ok) {
    const err = body && body.error;
    throw new ApiError(
      resp.status,
      (err && err.code) || `http_${resp.status}`,
      (err && typeof err.message === "string" && err.message) || `request failed (${resp.status})`,
      body,
    );
  }
  return body;
}

export class HttpApiClient {
  constructor(tenantId) {
    this.tenantId = tenantId;
    this.token = null;
  }

  setAuthToken(token) {
    this.token = token || null;
  }

  url(path) {
    return `${API_BASE}/tenants/${encodeURIComponent(this.tenantId)}${path}`;
  }

  async request(path, init) {
    const resp = await fetch(this.url(path), {
      ...init,
      headers: buildHeaders(this.tenantId, init && init.headers, this.token),
    });
    return parseResponse(resp);
  }

  // ---------- Auth ----------

  async register(payload) {
    const resp = await fetch(`${API_BASE}/auth/register`, {
      method: "POST",
      headers: { "content-type": "application/json", Accept: "application/json" },
      body: JSON.stringify(payload),
    });
    return parseResponse(resp);
  }

  async login(payload) {
    const resp = await fetch(`${API_BASE}/auth/login`, {
      method: "POST",
      headers: { "content-type": "application/json", Accept: "application/json" },
      body: JSON.stringify(payload),
    });
    return parseResponse(resp);
  }

  // Not tenant-scoped: the signed-in user's own password (the only call open while it is temporary).
  async changePassword(currentPassword, newPassword) {
    const resp = await fetch(`${API_BASE}/auth/change-password`, {
      method: "POST",
      headers: buildHeaders(this.tenantId, { "content-type": "application/json" }, this.token),
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    });
    return parseResponse(resp);
  }

  async getMe() {
    const headers = new Headers({ Accept: "application/json" });
    if (this.token) headers.set("Authorization", `Bearer ${this.token}`);
    const resp = await fetch(`${API_BASE}/auth/me`, { headers });
    return parseResponse(resp);
  }

  async getTenant(tenantId) {
    const resp = await fetch(`${API_BASE}/tenants/${encodeURIComponent(tenantId)}`, {
      headers: { Accept: "application/json" },
    });
    return parseResponse(resp);
  }

  // Back-compat shim for code that still calls getRoles().
  async getRoles() {
    if (!this.token) throw new ApiError(401, "unauthenticated", "not signed in");
    return this.getMe();
  }

  getDashboard() {
    return this.request("/dashboard");
  }

  async listProducts(filter) {
    const params = new URLSearchParams();
    if (filter && filter.status) params.set("status", filter.status);
    if (filter && filter.q) params.set("q", filter.q);
    // The whole catalogue (the API's ceiling): names and training are joined on it.
    params.set("limit", String((filter && filter.limit) || 500));
    const qs = `?${params}`;
    const data = await this.request(`/products${qs}`);
    return Array.isArray(data) ? data : data.data;
  }

  getProduct(id) {
    return this.request(`/products/${id}`);
  }

  createProduct(req) {
    return this.request(`/products`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(req),
    });
  }

  updateProduct(id, req) {
    return this.request(`/products/${id}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(req),
    });
  }

  uploadProductImages(id, files, onProgress) {
    const form = new FormData();
    for (const f of files.training) form.append("training_images", f, f.name);
    form.append("test_image", files.test, files.test.name);

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", this.url(`/products/${id}/images`));
      xhr.setRequestHeader("x-tenant-id", this.tenantId);
      if (this.token) xhr.setRequestHeader("Authorization", `Bearer ${this.token}`);
      xhr.upload.onprogress = (e) => {
        if (onProgress && e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
      };
      xhr.onload = () => {
        try {
          const body = xhr.responseText ? JSON.parse(xhr.responseText) : {};
          if (xhr.status >= 200 && xhr.status < 300) resolve(body);
          else
            reject(
              new ApiError(
                xhr.status,
                (body && body.error && body.error.code) || "upload_failed",
                (body && body.error && body.error.message) || xhr.statusText,
                body,
              ),
            );
        } catch (err) {
          reject(err);
        }
      };
      xhr.onerror = () => reject(new ApiError(0, "network_error", "upload failed"));
      xhr.send(form);
    });
  }

  preprocessProduct(id) {
    return this.request(`/products/${id}/preprocess`, { method: "POST" });
  }

  validateProduct(id) {
    return this.request(`/products/${id}/validate`, { method: "POST" });
  }

  suggestProductName(id) {
    return this.request(`/products/${id}/suggest-name`, { method: "POST" });
  }

  // One photo into one pose slot (kind training: index 1..; kind test), as the capture app streams them.
  uploadProductPhotoSingle(id, { file, kind = "training", index, position }) {
    const form = new FormData();
    form.append("image", file, file.name);
    form.append("kind", kind);
    if (kind === "training") form.append("index", String(index));
    if (position) form.append("position", position);
    return fetch(this.url(`/products/${id}/images/single`), {
      method: "POST", headers: buildHeaders(this.tenantId, undefined, this.token), body: form,
    }).then(parseResponse);
  }

  setEnrolmentSetActive(typeId, setId, active) {
    return this.request(`/cabinet-types/${encodeURIComponent(typeId)}/enrolment/${encodeURIComponent(setId)}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ active }),
    });
  }

  listProductImages(id, kind) {
    return this.request(`/products/${id}/images?kind=${kind}`);
  }

  startTraining(req) {
    return this.request(`/training/jobs`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(req),
    });
  }

  // ---------- Gallery & Edge Bundle (new RP5 algorithm) ----------

  async listGalleries() {
    const data = await this.request(`/galleries`);
    return data.items || [];
  }

  createGalleryBuild(req = {}) {
    // req: { product_uuids?: string[], encoder_version?: string, label?: string }
    return this.request(`/galleries/builds`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(req),
    });
  }

  getGalleryBuild(buildId) {
    return this.request(`/galleries/builds/${encodeURIComponent(buildId)}`);
  }

  activateGallery(galleryId) {
    return this.request(`/galleries/${encodeURIComponent(galleryId)}/activate`, {
      method: "POST",
    });
  }

  createEdgeBundle(galleryId, req = {}) {
    // req: { target_profiles?: string[] }
    return this.request(`/galleries/${encodeURIComponent(galleryId)}/edge-bundles`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(req),
    });
  }

  async listEdgeBundles({ galleryId } = {}) {
    const qs = galleryId ? `?gallery_id=${encodeURIComponent(galleryId)}` : "";
    const data = await this.request(`/edge-bundles${qs}`);
    return data.items || [];
  }

  listTrainingJobs() {
    return this.request(`/training/jobs`);
  }

  getTrainingJob(id) {
    return this.request(`/training/jobs/${id}`);
  }

  listModels() {
    return this.request(`/models`);
  }

  async evaluateModel(modelId, image) {
    const form = new FormData();
    form.append("image", image, image.name);
    const headers = { "x-tenant-id": this.tenantId };
    if (this.token) headers["Authorization"] = `Bearer ${this.token}`;
    const resp = await fetch(this.url(`/models/${modelId}/evaluate`), {
      method: "POST",
      headers,
      body: form,
    });
    return parseResponse(resp);
  }

  testModelOnPhotos(modelId, body = {}) {
    return this.request(`/models/${modelId}/test-photos`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
  }

  listDevices() {
    return this.request(`/devices`);
  }

  getDevice(deviceId) {
    return this.request(`/devices/${deviceId}`);
  }

  getDevicePipelineLog(deviceId, { limit = 100 } = {}) {
    const qs = limit ? `?limit=${encodeURIComponent(limit)}` : "";
    return this.request(`/devices/${deviceId}/pipeline-log${qs}`);
  }

  registerDevice(payload) {
    return this.request(`/devices`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload),
    });
  }

  updateDevice(deviceId, payload) {
    return this.request(`/devices/${deviceId}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload),
    });
  }

  revokeDevice(deviceId) {
    return this.request(`/devices/${deviceId}`, { method: "DELETE" });
  }

  deployModel(req) {
    return this.request(`/deployments`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(req),
    });
  }

  listDeployments({ limit = 50 } = {}) {
    const qs = limit ? `?limit=${encodeURIComponent(limit)}` : "";
    return this.request(`/deployments${qs}`);
  }

  rollbackDevice(deviceId) {
    return this.request(`/devices/${deviceId}/rollback`, { method: "POST" });
  }

  // ---------- Remote access & console ----------

  // `mode` here is the mode actually in force; `requested_mode` is what was
  // asked for. They differ once a grant expires — render `mode`.
  getDeviceAccess(deviceId) {
    return this.request(`/devices/${deviceId}/access`);
  }

  // tenant_admin only. A reason is mandatory and the TTL is capped server-side.
  grantDeviceFullAccess(deviceId, { ttlMinutes = 60, reason }) {
    return this.request(`/devices/${deviceId}/access`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ mode: "full", ttl_minutes: ttlMinutes, reason }),
    });
  }

  revokeDeviceFullAccess(deviceId) {
    return this.request(`/devices/${deviceId}/access`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ mode: "standard" }),
    });
  }

  // Short-lived, group-scoped Web PubSub token. Ask only for the capabilities
  // this view needs: "pty" is refused unless the caller is tenant_admin and the
  // device is in full mode.
  createConsoleToken(deviceId, capabilities = ["logs", "cmd"]) {
    return this.request(`/devices/${deviceId}/console/token`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ capabilities }),
    });
  }

  rotateAgentKey(deviceId) {
    return this.request(`/devices/${deviceId}/agent/key`, { method: "POST" });
  }

  async listSessions(filter) {
    const params = new URLSearchParams();
    for (const key of ["device_id", "status", "limit"]) {
      if (filter && filter[key] != null && filter[key] !== "") params.set(key, filter[key]);
    }
    const qs = params.toString() ? `?${params}` : "";
    const r = await this.request(`/sessions${qs}`);
    // The backend answers {items, count}; the mock client answers a bare array.
    return r && r.items ? r.items : r;
  }

  // ---------- Purchases (docs/tasks/31) ----------

  async listPurchases(filter) {
    const params = new URLSearchParams();
    for (const key of ["from", "to", "basket", "device_id", "outcome", "limit"]) {
      if (filter && filter[key] != null && filter[key] !== "") params.set(key, filter[key]);
    }
    const qs = params.toString() ? `?${params}` : "";
    const r = await this.request(`/purchases${qs}`);
    return r && r.purchases ? r.purchases : [];
  }

  getPurchase(sessionId) {
    return this.request(`/purchases/${encodeURIComponent(sessionId)}`);
  }

  // The basket as the sales platform holds it: prices, payment, corrections (docs/tasks/37).
  getPurchaseBasket(sessionId) {
    return this.request(`/purchases/${encodeURIComponent(sessionId)}/basket`);
  }

  // body: {request_id, note, lines: [{product_uuid | ean_code, quantity}], dry_run}
  correctPurchase(sessionId, body) {
    return this.request(`/purchases/${encodeURIComponent(sessionId)}/corrections`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
  }

  // ---------- Webhooks ----------

  listWebhooks() {
    return this.request(`/webhooks`);
  }

  createWebhook(payload) {
    return this.request(`/webhooks`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload),
    });
  }

  deleteWebhook(webhookId) {
    return this.request(`/webhooks/${webhookId}`, { method: "DELETE" });
  }

  // ---------- Built-in test webhook sink ----------

  testWebhookSinkUrl() {
    return `${API_BASE}/test-webhook/${encodeURIComponent(this.tenantId)}`;
  }

  listTestWebhookEvents({ limit = 100 } = {}) {
    const qs = limit ? `?limit=${encodeURIComponent(limit)}` : "";
    return this.request(`/test-webhook/events${qs}`);
  }

  clearTestWebhookEvents() {
    return this.request(`/test-webhook/events`, { method: "DELETE" });
  }

  // ---------- Device commands & live frames ----------

  sendDeviceCommand(deviceId, commandType, payload = {}) {
    return this.request(`/devices/${deviceId}/commands`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ command_type: commandType, payload }),
    });
  }

  // The merchant's own door call, not a raw command. For a virtual cabinet
  // (hardware "emulator") the backend runs the whole visit here and now and
  // answers with the cart it settled; for a real one it enqueues the same
  // `door.open` the command endpoint would.
  openCabinetDoor(deviceId, body = {}) {
    return this.request(`/devices/${deviceId}/door/open`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
  }

  // The cloud's own view of the door: transitions the cabinet pushed, the
  // acks of door commands and the health report, newest first. For a virtual
  // cabinet it is the only view — it publishes no pipeline log.
  getDeviceDoorState(deviceId) {
    return this.request(`/devices/${deviceId}/door`);
  }

  // The hand in a virtual cabinet: takes one product out of the *open* visit
  // and shuts the door. 409 while no visit is open, which is what keeps the
  // console's button disabled until the merchant has opened the cabinet.
  sellFromEmulator(deviceId, body = {}) {
    return this.request(`/devices/${deviceId}/emulator/sell`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
  }

  listDeviceCommands(deviceId, { pending = false, limit = 50 } = {}) {
    const params = new URLSearchParams();
    if (pending) params.set("pending", "true");
    if (limit) params.set("limit", String(limit));
    const qs = params.toString() ? `?${params}` : "";
    return this.request(`/devices/${deviceId}/commands${qs}`);
  }

  getDeviceFrameUrl(deviceId) {
    return this.request(`/devices/${deviceId}/frame`);
  }

  // ---------- Fleet: cabinet types and one cabinet (docs/tasks/35) ----------

  async listCabinetTypes() {
    const data = await this.request(`/cabinet-types`);
    return Array.isArray(data) ? data : data?.cabinet_types || [];
  }

  createCabinetType(body) {
    return this.request(`/cabinet-types`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
  }

  updateCabinetType(typeId, body) {
    return this.request(`/cabinet-types/${encodeURIComponent(typeId)}`, {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
  }

  getCabinetTypeTrainingStatus(typeId) {
    return this.request(`/cabinet-types/${encodeURIComponent(typeId)}/training/status`);
  }

  async listEnrolmentSets(typeId) {
    const data = await this.request(`/cabinet-types/${encodeURIComponent(typeId)}/enrolment`);
    return Array.isArray(data) ? data : data?.sets || [];
  }

  // {device_id, cabinet_type: {...} | null}
  getDeviceCabinetType(deviceId) {
    return this.request(`/devices/${deviceId}/cabinet-type`);
  }

  setDeviceCabinetType(deviceId, cabinetTypeId) {
    return this.request(`/devices/${deviceId}/cabinet-type`, {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ cabinet_type_id: cabinetTypeId }),
    });
  }

  // The cabinet's measured lenses and light (docs/tasks/39), for the corrected
  // live view: {device_id, calibration: {schema, cameras: {"0": {lens, light}}} | null, updated_at}
  getDeviceCalibration(deviceId) {
    return this.request(`/devices/${deviceId}/calibration`);
  }

  putDeviceCalibration(deviceId, calibration) {
    return this.request(`/devices/${deviceId}/calibration`, {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(calibration),
    });
  }

  // "Is this cabinet fit to sell?" - status, issues, warnings and the device's own report.
  getDeviceHealth(deviceId) {
    return this.request(`/devices/${deviceId}/health`);
  }

  getDeviceCabinetTemperature(deviceId) {
    return this.request(`/devices/${deviceId}/cabinet-temperature`);
  }

  // 404 means the cabinet never reported a weight board.
  getDeviceWeight(deviceId) {
    return this.request(`/devices/${deviceId}/weight`);
  }

  // The cloud's models; probe=true wakes the classifier container to read its classes (slow when cold).
  getModelsInventory({ probe = false } = {}) {
    return this.request(`/models/inventory${probe ? "?probe=classifier" : ""}`);
  }

  getRecognitionSettings() {
    return this.request(`/recognition-settings`);
  }

  // The cloud classifier and its retraining (docs/tasks/38).
  getClassifier() {
    return this.request(`/classifier`);
  }

  startClassifierRun(reason) {
    return this.request(`/classifier/runs`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ reason }),
    });
  }

  getDeviceShelfUpdate(deviceId) {
    return this.request(`/devices/${deviceId}/shelf-update`);
  }

  // "Update shelves": build, package and assign the cabinet's gallery on the server.
  startShelfUpdate(deviceId, productUuids, label) {
    return this.request(`/devices/${deviceId}/shelf-update`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ product_uuids: productUuids, ...(label ? { label } : {}) }),
    });
  }

  getDevicePlanogram(deviceId) {
    return this.request(`/devices/${deviceId}/planogram`);
  }

  getDeviceShelves(deviceId) {
    return this.request(`/devices/${deviceId}/shelves`);
  }

  // A weight-tray action through the command queue; the call waits for the cabinet's answer (200 done, 409 refused, 202 still queued).
  async postWeightAction(deviceId, body) {
    const resp = await fetch(this.url(`/devices/${deviceId}/weight/actions`), {
      method: "POST",
      headers: buildHeaders(this.tenantId, { "content-type": "application/json" }, this.token),
      body: JSON.stringify({ wait_s: 15, ...body }),
    });
    let data = null;
    try { data = await resp.json(); } catch { data = null; }
    return { status: resp.status, data };
  }

  async listUsers() {
    const data = await this.request(`/users`);
    return data?.users || [];
  }

  createUser(body) {
    return this.request(`/users`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
  }

  updateUser(userId, body) {
    return this.request(`/users/${encodeURIComponent(userId)}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
  }

  async listApiKeys() {
    const data = await this.request(`/api-keys`);
    return data?.items || [];
  }

  // The plaintext key is in this answer only.
  createApiKey(body) {
    return this.request(`/api-keys`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
  }

  revokeApiKey(id) {
    return this.request(`/api-keys/${encodeURIComponent(id)}`, { method: "DELETE" });
  }

  getSessionCloudChecks(sessionId) {
    return this.request(`/sessions/${sessionId}/cloud-checks`);
  }

  getCodeDeploymentCurrent(deviceId) {
    return this.request(`/devices/${deviceId}/code-deployments/current`);
  }

  assignCodeRelease(deviceId, version) {
    return this.request(`/devices/${deviceId}/code-deployments`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ version }),
    });
  }

  setRecognitionSettings(body) {
    return this.request(`/recognition-settings`, {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
  }

  getDeviceTrainingStatus(deviceId) {
    return this.request(`/devices/${deviceId}/training/status`);
  }

  async listCodeReleases() {
    const data = await this.request(`/code-releases`);
    return Array.isArray(data) ? data : data?.releases || data?.code_releases || [];
  }
}
